import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { loadModuleEntry } from './moduleEntryLoader';
import nativeFetch from './nativeFetch';

/**
 * ModuleHost — mounts a custom module's frontend into the shell (plan Task 7.6).
 *
 * This is the no-iframe half of D9. A custom module's UI is an ESM bundle the runtime
 * serves from the tenant's own origin; the shell lazy-imports it and hands it a DOM node
 * and a context object. The module renders whatever it likes with whatever framework it
 * likes — the contract is two exported functions:
 *
 *     export function mount(el, ctx) { ... }
 *     export function unmount() { ... }
 *
 * D10's framework-agnosticism is why this is a plain DOM node and not a React portal: a
 * portal would couple every module to this app's React version, and a runtime frontend
 * upgrade would then strand every installed module — the exact failure the SDK's
 * additive-only promise exists to prevent.
 *
 * ## The residual risk, stated where the code is
 *
 * Dropping the iframe drops the browser's origin sandbox (plan §6.2). A module authored
 * by one tenant and installed into another runs in the same origin as the CRM shell: it
 * can read `crm_token` from localStorage and call any Core API directly, bypassing the
 * SDK's AND composition and its audit log. The control plane's audit gate is the only
 * control, and gate checks G6/G6b reject any bundle that references `crm_token`,
 * `localStorage`, or fetches `/api/` outside its own namespace. Not passing the raw token
 * in `ctx` is hygiene, not a boundary — a module that wants it can read it. Do not let the
 * absence of `crm_token` below be read as isolation.
 *
 * `ctx.fetch` follows from that same reasoning rather than contradicting it. Because G6
 * forbids a *compliant* module from reading the session, and the runtime authenticates
 * with a Bearer header that no login sets as a cookie, a module had no way to call its own
 * backend at all — the documented path returned `401`. The shell therefore injects the
 * header on the module's behalf, scoped to the module's own prefix, and still never hands
 * over the credential itself.
 *
 * ## Cleanup is the real risk, not DOM size (D11)
 *
 * With many modules installed, the thing that degrades a long session is leaked state:
 * a `<style>` tag from a module the user visited an hour ago, a listener on `window`, a
 * timer. So unmount is enforced by the shell rather than trusted to the module:
 *
 *   1. `unmount()` is awaited, so an async teardown finishes before anything is removed.
 *   2. The subtree is hard-removed — every node inside it, and every listener attached
 *      to those nodes, goes with it.
 *   3. Stylesheets are removed. Ours are tagged `data-module` when we create them; ones
 *      the module injected itself are found by diffing `document.head` across the mount
 *      and tagged the same way, so "the module's styles" is observed rather than assumed.
 *
 * What the shell cannot reclaim: listeners on `window`/`document` and timers the module
 * created. Those are `unmount()`'s job, the cookbook says so, and there is no way to
 * enforce it from here — stated plainly rather than implied to be handled.
 */

/** Attribute both the container and every stylesheet the shell attributes to a module. */
const MODULE_ATTR = 'data-module';

/** Selector for the stylesheet nodes a mount may add. */
const STYLE_SELECTOR = 'style, link[rel="stylesheet"]';

function styleNodes() {
    return Array.from(document.querySelectorAll(STYLE_SELECTOR));
}

/**
 * Load a module's declared stylesheets, and resolve when they have applied.
 *
 * Awaited before `mount()` so the module's first paint is styled. Without it the user
 * sees an unstyled flash on every visit — brief, but it is the first thing they see, and
 * it makes a module look broken rather than loading.
 *
 * A failed stylesheet resolves rather than rejects: a module missing its CSS is degraded,
 * not broken, and refusing to mount over it would turn a cosmetic fault into a blank page.
 */
function loadStyles(slug, urls) {
    return Promise.all(urls.map((href) => new Promise((resolve) => {
        const existing = document.querySelector(`link[${MODULE_ATTR}="${slug}"][href="${href}"]`);
        if (existing) { resolve(); return; }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute(MODULE_ATTR, slug);
        link.addEventListener('load', () => resolve(), { once: true });
        link.addEventListener('error', () => {
            console.warn(`[ModuleHost] ${slug}: stylesheet failed to load: ${href}`);
            resolve();
        }, { once: true });
        document.head.appendChild(link);
    })));
}

function removeStyles(slug) {
    document.querySelectorAll(`[${MODULE_ATTR}="${slug}"]`).forEach((node) => {
        if (node.matches(STYLE_SELECTOR)) node.remove();
    });
}

/**
 * Tag stylesheets that appeared during a mount so unmount can remove them.
 *
 * `before` is the snapshot taken immediately before the dynamic import. Anything in the
 * document now that was not in it then, and is not already claimed by another module, was
 * put there by this one.
 */
function claimNewStyles(slug, before) {
    const seen = new Set(before);
    styleNodes().forEach((node) => {
        if (seen.has(node)) return;
        if (node.hasAttribute(MODULE_ATTR)) return;
        node.setAttribute(MODULE_ATTR, slug);
    });
}

function FailureState({ slug, name, reason, onRetry }) {
    return (
        <div className="flex items-center justify-center h-full min-h-[400px]" data-module-error={slug}>
            <div className="text-center max-w-md">
                <h2 className="text-lg font-semibold text-ink-1 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {name || slug} could not be loaded
                </h2>
                <p className="text-sm text-ink-2 mb-4">
                    Its interface failed to start. The rest of the CRM is unaffected.
                </p>
                {reason ? (
                    <pre
                        className="text-xs text-left p-3 rounded-lg overflow-x-auto mb-4"
                        style={{ background: 'var(--surface-2, rgba(0,0,0,0.04))', color: 'var(--ink-2)' }}
                    >{reason}</pre>
                ) : null}
                {onRetry ? (
                    <button
                        onClick={onRetry}
                        className="px-4 py-2 text-sm font-medium rounded-lg"
                        style={{ background: 'var(--ink-1)', color: 'var(--canvas)' }}
                    >
                        Try again
                    </button>
                ) : null}
            </div>
        </div>
    );
}

/**
 * Catches render errors thrown by anything React renders inside the host.
 *
 * Narrow by design: a module's own render happens outside React, so the failure that
 * actually matters — a throwing `mount()` — is async and cannot reach an error boundary.
 * That one is caught by the try/catch in the mount effect. This exists so a fault in the
 * host's own JSX degrades to the same contained state instead of a white screen.
 */
class ModuleErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        console.error(`[ModuleHost] ${this.props.slug} render error`, error, info);
    }

    render() {
        if (this.state.error) {
            return (
                <FailureState
                    slug={this.props.slug}
                    name={this.props.name}
                    reason={String(this.state.error?.message || this.state.error)}
                />
            );
        }
        return this.props.children;
    }
}

function LoadingState({ name }) {
    return (
        <div className="flex items-center justify-center h-full min-h-[400px]">
            <div className="text-center">
                <div
                    className="w-8 h-8 border-[3px] rounded-full animate-spin mx-auto mb-3"
                    style={{ borderColor: 'var(--line)', borderTopColor: 'var(--ink-1)' }}
                />
                <p className="text-ink-2 text-sm">Loading {name}…</p>
            </div>
        </div>
    );
}

export function ModuleHost({ slug: slugProp }) {
    const params = useParams();
    const slug = slugProp || params.slug;
    const location = useLocation();
    const navigate = useNavigate();
    const { user, getAuthHeader } = useAuth();
    const { theme } = useTheme();
    const { customModules, loading } = useWorkspace();

    const containerRef = useRef(null);
    const instanceRef = useRef(null);
    const themeSubscribers = useRef(new Set());
    const navSubscribers = useRef(new Set());

    const [status, setStatus] = useState('loading');
    const [failure, setFailure] = useState(null);
    const [attempt, setAttempt] = useState(0);

    const descriptor = useMemo(
        () => (customModules || []).find((item) => item.slug === slug) || null,
        [customModules, slug],
    );

    const basePath = descriptor?.base_path || (slug ? `/m/${slug}` : '');
    const subPath = basePath && location.pathname.startsWith(basePath)
        ? location.pathname.slice(basePath.length) || '/'
        : location.pathname;

    // Latest values without re-running the mount effect. A module is mounted once and
    // notified of changes; re-mounting on every theme toggle or navigation would discard
    // whatever state the user had built up inside it.
    const latest = useRef({ subPath, theme });
    latest.current = { subPath, theme };

    const buildContext = useCallback(() => ({
        // The acting user, projected — not the raw document. A module gets what it needs
        // to render ("Hi Alice", role-dependent affordances) and no more; anything
        // authoritative it needs comes from its own API, where the AND composition applies.
        user: user ? {
            user_id: user.user_id,
            email: user.email,
            name: user.name || user.full_name || null,
            role: user.role || null,
            is_admin: !!user.is_admin,
        } : null,
        org: user ? { org_id: user.org_id || null, name: user.org_name || null } : null,
        theme: latest.current.theme,
        basePath,
        path: latest.current.subPath,
        // Where the module's own backend lives. Exposed for display and for anything that
        // needs the literal prefix; `ctx.fetch` below is what a module should actually call.
        apiBase: descriptor?.api_base || `/api/modules/${slug}`,
        /**
         * An authenticated `fetch`, pre-scoped to this module's own API prefix.
         *
         * **This exists because a module cannot authenticate itself.** Gate check G6
         * rejects any bundle that references `crm_token` or `localStorage`, so module code
         * structurally cannot read the session — and the runtime authenticates with a
         * Bearer header, not a cookie: `POST /auth/login` sets none (only the Google
         * session route does). So a bundle doing `fetch(apiBase + '/x')`, even with
         * `credentials: 'same-origin'`, sends no credential at all and gets a `401`. The
         * starter module shipped exactly that pattern and its UI could not load its own
         * data on a password session.
         *
         * The token is injected here and never handed to the module, so G6 stays as strict
         * as it was and the §6.2 posture is unchanged: this is not isolation — a module
         * runs in the shell's origin and could read the session itself — it is the
         * difference between the documented path being clean and the documented path being
         * broken.
         *
         * `path` is relative to `apiBase`. An absolute URL or a `..` escape is refused
         * rather than silently rewritten, because a module reaching outside its own prefix
         * is what G6b rejects at submission and this is the same rule at runtime.
         */
        fetch: (path, init = {}) => {
            const base = descriptor?.api_base || `/api/modules/${slug}`;
            const rel = typeof path === 'string' ? path : '';
            if (/^[a-z][a-z0-9+.-]*:/i.test(rel) || rel.startsWith('//')) {
                return Promise.reject(
                    new Error(
                        `ctx.fetch takes a path relative to ${base}, not an absolute URL ` +
                        `(got "${rel}"). A module may only call its own API.`,
                    ),
                );
            }
            const joined = `${base}${rel.startsWith('/') ? '' : '/'}${rel}`;
            if (joined.split('/').includes('..')) {
                return Promise.reject(
                    new Error(`ctx.fetch refuses "${rel}": it escapes ${base}.`),
                );
            }
            // `nativeFetch`, NOT `window.fetch` (S11). A module shares this realm,
            // so it can replace `window.fetch` with a wrapper that reads the
            // `Authorization` header off the very next `ctx.fetch` call — capturing
            // the raw session token without ever naming `crm_token` or
            // `localStorage`, so every gate check still passes. The reference in
            // `nativeFetch.js` is bound when the shell's bundle evaluates, which is
            // before any module entry is even fetched.
            return nativeFetch(joined, {
                ...init,
                headers: { ...(init.headers || {}), ...getAuthHeader() },
            });
        },
        version: descriptor?.version || null,
        navigate: (path) => {
            if (typeof path !== 'string' || !path) return;
            navigate(path.startsWith('/') ? path : `${basePath}${path.startsWith('/') ? '' : '/'}${path}`);
        },
        showToast: (message, type) => {
            const fn = toast[type] || toast;
            try { fn(String(message)); } catch { toast(String(message)); }
        },
        onThemeChange: (cb) => {
            if (typeof cb !== 'function') return () => {};
            themeSubscribers.current.add(cb);
            return () => themeSubscribers.current.delete(cb);
        },
        // Not in Task 7.10's list, and added deliberately — see the run's snapshot. The
        // shell navigates with pushState, which fires no `popstate`, so a module with more
        // than one nav item would have no way to learn that its own sidebar was clicked.
        // Subscribe-shaped like `onThemeChange` rather than a new remount trigger, because
        // remounting on navigation would throw away the module's state on every click.
        onNavigate: (cb) => {
            if (typeof cb !== 'function') return () => {};
            navSubscribers.current.add(cb);
            return () => navSubscribers.current.delete(cb);
        },
    }), [user, basePath, descriptor, slug, navigate, getAuthHeader]);

    // ── mount / unmount ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!slug || loading) return undefined;

        if (!descriptor) {
            setStatus('missing');
            return undefined;
        }
        if (!descriptor.entry_url) {
            setStatus('failed');
            setFailure(`${descriptor.name || slug} declares no frontend entry point.`);
            return undefined;
        }

        let cancelled = false;
        // Refs captured as locals so the cleanup below reads stable cells
        // (react-hooks/exhaustive-deps: ref.current in cleanup).
        const themeSubs = themeSubscribers.current;
        const navSubs = navSubscribers.current;
        const container = containerRef.current;
        setStatus('loading');
        setFailure(null);

        (async () => {
            const stylesBefore = styleNodes();
            try {
                await loadStyles(slug, descriptor.style_urls || []);
                const mod = await loadModuleEntry(descriptor.entry_url);
                if (cancelled) return;

                if (typeof mod.mount !== 'function') {
                    throw new Error("the entry does not export a `mount(el, ctx)` function");
                }
                await mod.mount(container, buildContext());
                if (cancelled) {
                    // Mounted into a container this effect no longer owns. Tear it down
                    // now rather than leaving it live — StrictMode's double-invoked
                    // effects make this the normal path in development, not an edge case.
                    try { await mod.unmount?.(); } catch { /* teardown of a discarded mount */ }
                    return;
                }
                // Called once, here, and once in the `catch` below — both against the same
                // pre-import snapshot, so between them they claim anything added by the
                // import itself *and* anything added by `mount()`. A third call before
                // `mount` looked like belt-and-braces and was strictly redundant; mutation
                // testing proved it by deleting it with the suite still green.
                claimNewStyles(slug, stylesBefore);
                instanceRef.current = mod;
                setStatus('mounted');
            } catch (error) {
                if (cancelled) return;
                console.error(`[ModuleHost] ${slug} failed to mount`, error);
                claimNewStyles(slug, stylesBefore);
                setFailure(String(error?.message || error));
                setStatus('failed');
            }
        })();

        return () => {
            cancelled = true;
            const mod = instanceRef.current;
            instanceRef.current = null;
            themeSubs.clear();
            navSubs.clear();

            // Sequenced, not raced: `unmount()` is given the chance to finish before the
            // DOM it is tearing down disappears underneath it. The removal then happens
            // regardless — a module that throws or hangs in `unmount()` must not be able
            // to leave its subtree and stylesheets behind.
            const finish = () => {
                if (container) container.replaceChildren();
                removeStyles(slug);
            };
            try {
                const result = mod?.unmount?.();
                if (result && typeof result.then === 'function') {
                    result.then(finish, (error) => {
                        console.error(`[ModuleHost] ${slug} unmount rejected`, error);
                        finish();
                    });
                } else {
                    finish();
                }
            } catch (error) {
                console.error(`[ModuleHost] ${slug} unmount threw`, error);
                finish();
            }
        };
        // `buildContext` is deliberately absent: it changes on every theme toggle and
        // navigation, and including it would remount the module on both. The mount reads
        // it once and subsequent changes arrive through the subscriber callbacks below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, descriptor?.entry_url, descriptor?.version, loading, attempt]);

    // Theme and navigation reach a mounted module as notifications, not remounts.
    useEffect(() => {
        themeSubscribers.current.forEach((cb) => {
            try { cb(theme); } catch (error) { console.error(`[ModuleHost] ${slug} onThemeChange handler threw`, error); }
        });
    }, [theme, slug]);

    useEffect(() => {
        navSubscribers.current.forEach((cb) => {
            try { cb(subPath); } catch (error) { console.error(`[ModuleHost] ${slug} onNavigate handler threw`, error); }
        });
    }, [subPath, slug]);

    const retry = useCallback(() => setAttempt((n) => n + 1), []);

    if (loading && !descriptor) {
        return <LoadingState name={slug} />;
    }

    if (status === 'missing') {
        return (
            <FailureState
                slug={slug}
                name={slug}
                reason={
                    `No custom module '${slug}' is installed and serving on this runtime. ` +
                    'If it was just installed, the container may still be restarting; if it ' +
                    'is installed but refused to load, GET /api/health/modules reports why.'
                }
            />
        );
    }

    return (
        <ModuleErrorBoundary slug={slug} name={descriptor?.name}>
            {status === 'loading' ? <LoadingState name={descriptor?.name || slug} /> : null}
            {status === 'failed' ? (
                <FailureState slug={slug} name={descriptor?.name} reason={failure} onRetry={retry} />
            ) : null}
            {/* Always rendered, and never conditionally: the mount effect captures this
                node, so unmounting it while a module lives inside would hand the module a
                detached container and leak whatever it attached to it. Hidden rather than
                removed while loading or failed. */}
            <div
                ref={containerRef}
                {...{ [MODULE_ATTR]: slug }}
                data-module-version={descriptor?.version || ''}
                style={{ display: status === 'mounted' ? 'contents' : 'none' }}
            />
        </ModuleErrorBoundary>
    );
}

export default ModuleHost;
